import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import pngNewsService from '../../../services/pngNewsService';
import alertService from '../../../services/alertService';

const LiveUpdatesSection = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('news'); // 'news' or 'alerts'

  useEffect(() => {
    loadUpdates();

    // Subscribe to real-time news updates
    const newsChannel = pngNewsService?.subscribeToNews((newNews) => {
      setNews(prev => [newNews, ...prev]?.slice(0, 5));
    });

    return () => {
      if (newsChannel) {
        newsChannel?.unsubscribe();
      }
    };
  }, []);

  const loadUpdates = async () => {
    try {
      setLoading(true);
      const [newsData, alertsData] = await Promise.all([
        pngNewsService?.getRecentNews(5),
        alertService?.getRecentAlerts(5)
      ]);
      setNews(newsData || []);
      setAlerts(alertsData || []);
    } catch (error) {
      console.error('Failed to load updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      hr: 'bg-blue-100 text-blue-700',
      payroll: 'bg-green-100 text-green-700',
      tax: 'bg-yellow-100 text-yellow-700',
      minimum_wage: 'bg-red-100 text-red-700',
      employment_act: 'bg-purple-100 text-purple-700',
      gazette: 'bg-indigo-100 text-indigo-700',
      general: 'bg-gray-100 text-gray-700'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      low: 'bg-blue-100 text-blue-700 border-blue-300'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      critical: 'AlertTriangle',
      high: 'AlertCircle',
      medium: 'Info',
      low: 'Bell'
    };
    return icons?.[severity] || 'Bell';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      hr: 'HR',
      payroll: 'Payroll',
      tax: 'Tax',
      minimum_wage: 'Min Wage',
      employment_act: 'Employment Act',
      gazette: 'Gazette',
      general: 'General'
    };
    return labels?.[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date?.toLocaleDateString('en-PG', {
      day: '2-digit',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Icon name="Radio" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Live Updates & News</h2>
            <p className="text-sm text-muted-foreground">Government news, compliance alerts, and important updates</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/government-news-updates-center')}
        >
          View All
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-border">
        <button
          onClick={() => setActiveTab('news')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'news' ?'text-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon name="Newspaper" size={16} />
            <span>Government News</span>
            {news?.length > 0 && (
              <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                {news?.length}
              </span>
            )}
          </div>
          {activeTab === 'news' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'alerts' ?'text-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={16} />
            <span>Compliance Alerts</span>
            {alerts?.length > 0 && (
              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                {alerts?.length}
              </span>
            )}
          </div>
          {activeTab === 'alerts' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
          )}
        </button>
      </div>
      {/* Content */}
      <div className="space-y-3">
        {activeTab === 'news' && (
          <>
            {news?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Newspaper" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No recent news updates</p>
              </div>
            ) : (
              news?.map((item) => (
                <div
                  key={item?.id}
                  className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-all cursor-pointer border border-transparent hover:border-primary/20"
                  onClick={() => navigate('/government-news-updates-center')}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(item?.category)}`}>
                        {getCategoryLabel(item?.category)}
                      </span>
                      {item?.priority === 'critical' && (
                        <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                          <Icon name="AlertTriangle" size={12} />
                          Critical
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(item?.publicationDate || item?.publication_date)}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-1 line-clamp-2">
                    {item?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {item?.summary}
                  </p>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === 'alerts' && (
          <>
            {alerts?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No active compliance alerts</p>
              </div>
            ) : (
              alerts?.map((alert) => (
                <div
                  key={alert?.id}
                  className={`p-4 rounded-lg border-l-4 ${
                    alert?.severity === 'critical' ?'bg-red-50 border-red-500'
                      : alert?.severity === 'high' ?'bg-orange-50 border-orange-500'
                      : alert?.severity === 'medium' ?'bg-yellow-50 border-yellow-500' :'bg-blue-50 border-blue-500'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <Icon 
                        name={getSeverityIcon(alert?.severity)} 
                        size={16} 
                        className={`${
                          alert?.severity === 'critical' ?'text-red-600'
                            : alert?.severity === 'high' ?'text-orange-600'
                            : alert?.severity === 'medium' ?'text-yellow-600' :'text-blue-600'
                        }`}
                      />
                      <span className={`text-xs font-semibold uppercase ${
                        alert?.severity === 'critical' ?'text-red-700'
                          : alert?.severity === 'high' ?'text-orange-700'
                          : alert?.severity === 'medium' ?'text-yellow-700' :'text-blue-700'
                      }`}>
                        {alert?.severity} Priority
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(alert?.createdAt || alert?.created_at)}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">
                    {alert?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    {alert?.message}
                  </p>
                  {alert?.actionRequired && (
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          navigate('/compliance-overview');
                        }}
                      >
                        Take Action
                        <Icon name="ArrowRight" size={14} className="ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LiveUpdatesSection;